import {Outlet, useNavigate} from 'react-router-dom'
import {useAdminMe} from '@features/admin/queries/useAdminMe'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams'
import {Spinner} from '@components/ui/spinner'
import {useImpersonateExchange} from '@features/admin/queries/useImpersonateExchange'
import {useAuthStore} from '@features/user/store/store'
import {routes} from '@utilities/constants/routes'
import {errorHandler, isInternalPath} from '@utilities/helpers'
import {useEffect, useRef} from 'react'
import {z} from 'zod'

export const ImpersonateParamSchema = z.object({
    // One-time verification token issued by the admin app, exchanged for the real tokens
    impersonate: z.string().optional(),
    // Path to land on after the exchange, validated before use to avoid an open redirect
    route: z.string().optional()
})

export const ImpersonateRouteGuard = () => {
    const {searchParams, setSearchParams} = useParsedSearchParams(ImpersonateParamSchema)
    const navigate = useNavigate()
    const setTokens = useAuthStore(state => state.setTokens)
    // call admin me to validate admin token
    const adminMeQuery = useAdminMe()

    const impersonateToken = searchParams.impersonate
    // the params are stripped from the url before the exchange resolves, so the landing
    // path is kept here to still be available in onSuccess
    const impersonateRouteRef = useRef(searchParams.route)

    const impersonateExchangeMutation = useImpersonateExchange({
        onSuccess: data => {
            setTokens({
                accessToken: data.access_token,
                refreshToken: null,
                adminAccessToken: data.admin_access_token
            })
            const landingRoute = impersonateRouteRef.current
            navigate(isInternalPath(landingRoute) ? landingRoute : routes.HOME.handle.fullPath, {
                replace: true
            })
        },
        onError: error => {
            // the verification link is expired or already used: PrivateRoute redirects to login
            errorHandler(error)
        }
    })

    useEffect(() => {
        if (!impersonateToken) {
            return
        }
        // drop any previously stored session so the interceptor cannot attach a stale
        // adminAccessToken to this public request, and clear the previous user cached data
        useAuthStore.getState().resetAuthTokens()
        // remove the token from the url right after reading it, so it does not stick in history
        setSearchParams({impersonate: undefined, route: undefined})
        impersonateExchangeMutation.mutate({token: impersonateToken})
    }, [])

    if (impersonateToken || impersonateExchangeMutation.isPending || adminMeQuery.isLoading) {
        return (
            <div className="flex justify-center items-center h-full w-full">
                <Spinner size={40} fill="var(--color-neutral-500)" />
            </div>
        )
    }
    return <Outlet />
}
